/**
 * Wigglitz Lock Modal
 * Unlocks locked THEMES on the Build a Bundle page. "Series" below = a Wigglitz
 * theme (the "Theme" metaobject in admin, type `collection`); see the naming
 * note in snippets/series-schedule.liquid.
 *
 * 2026-09-25 (Haden): locking is per series. A series is locked while its
 * `locked_until` (Theme metaobject) is in the future; the page marks its
 * cards, group and dropdown tag with data-lock-series / data-lock-until.
 * Three ways it opens up, all remembered per browser:
 *   - its own unlock code  -> that series only   (localStorage seriesStorageKey)
 *   - the Klaviyo email    -> every series, now and future launches (storageKey)
 *   - its time passes      -> for everyone, even on a page Shopify cached
 *                             before the time (checked here, in the browser)
 * Codes never reach the browser: config.seriesCodes holds SHA-256 fingerprints
 * of each (trimmed, lowercased) code, compared with the fingerprint of the input.
 */

(function() {
  'use strict';

  var config = window.wigglitzLockConfig || {};
  var storageKey = config.storageKey || 'wigglitz-lock-unlocked';
  var seriesStorageKey = config.seriesStorageKey || 'wigglitz-lock-series';
  var seriesCodes = config.seriesCodes || {};

  // Pending action to execute after unlock
  var pendingAction = null;
  // Series whose locked card opened the modal (null = opened without one)
  var currentSeries = null;

  function nowSeconds() { return Date.now() / 1000; }

  /** Email unlock: everything, permanently. */
  function isAllUnlocked() {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch (e) {
      return false;
    }
  }

  /** Series unlocked with their own code. */
  function unlockedSeriesList() {
    try {
      var list = JSON.parse(localStorage.getItem(seriesStorageKey) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  /** Has this series' locked_until passed (per any element on the page)? */
  function isExpired(series) {
    var el = document.querySelector('[data-lock-series="' + series + '"][data-lock-until]');
    if (!el) return false;
    return nowSeconds() >= Number(el.getAttribute('data-lock-until'));
  }

  /**
   * No series: is EVERYTHING unlocked (email)?
   * With a series: can the shopper use that series right now?
   */
  function isUnlocked(series) {
    if (isAllUnlocked()) return true;
    if (!series) return false;
    return unlockedSeriesList().indexOf(series) !== -1 || isExpired(series);
  }

  /** Remove the locked look/behaviour from one series (or all when series is null). */
  function applyUnlock(series, root) {
    root = root || document;
    var sel = series ? '[data-lock-series="' + series + '"]' : '';

    root.querySelectorAll('.product-block--locked' + sel).forEach(function(card) {
      card.classList.remove('product-block--locked');
      var overlay = card.querySelector('.product-block__lock-overlay');
      if (overlay) overlay.style.display = 'none';
    });

    root.querySelectorAll('.product-tags__item--locked' + sel).forEach(function(tag) {
      var icon = tag.querySelector('.lock-icon');
      if (icon) icon.style.display = 'none';
      tag.classList.remove('product-tags__item--locked');
      tag.removeAttribute('data-locked');
    });

    // Reveal the "Add all" buttons (rendered hidden via this class so they can
    // come back the moment the series unlocks)
    root.querySelectorAll('.bundle-collection-group--locked' + sel).forEach(function(group) {
      group.classList.remove('bundle-collection-group--locked');
    });
  }

  /** Every series currently marked locked on the page, with its unlock time. */
  function lockedSeriesOnPage(root) {
    var found = {};
    (root || document).querySelectorAll('[data-lock-series][data-lock-until]').forEach(function(el) {
      found[el.getAttribute('data-lock-series')] = Number(el.getAttribute('data-lock-until'));
    });
    return found;
  }

  var expiryTimer = null;

  /**
   * Bring the page in line with what this browser has unlocked and the clock.
   * Safe to run repeatedly (removing an absent class is a no-op); custom.js
   * calls it again after adding cards.
   */
  function refresh(root) {
    if (isAllUnlocked()) {
      document.body.classList.add('lock-unlocked');
      applyUnlock(null, root);
      return;
    }
    var unlocked = unlockedSeriesList();
    var locked = lockedSeriesOnPage(root);
    var next = Infinity;
    Object.keys(locked).forEach(function(series) {
      if (unlocked.indexOf(series) !== -1 || nowSeconds() >= locked[series]) {
        applyUnlock(series, root);
      } else if (locked[series] < next) {
        next = locked[series];
      }
    });
    // A page left open across the unlock time opens up on its own.
    if (expiryTimer) clearTimeout(expiryTimer);
    var wait = (next - nowSeconds()) * 1000;
    if (next !== Infinity && wait < 86400000) {
      expiryTimer = setTimeout(function() { refresh(); }, Math.max(wait, 0) + 500);
    }
  }

  function unlockSeries(series) {
    var list = unlockedSeriesList();
    if (list.indexOf(series) === -1) list.push(series);
    try {
      localStorage.setItem(seriesStorageKey, JSON.stringify(list));
    } catch (e) {
      console.warn('Could not save unlock state to localStorage');
    }
    applyUnlock(series);
  }

  function unlockAll() {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {
      console.warn('Could not save unlock state to localStorage');
    }
    document.body.classList.add('lock-unlocked');
    applyUnlock(null);
  }

  /** SHA-256 hex of the trimmed, lowercased input (matches Liquid's `sha256`). */
  function fingerprint(text) {
    if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
      return Promise.reject(new Error('No Web Crypto'));
    }
    var data = new TextEncoder().encode(text.trim().toLowerCase());
    return window.crypto.subtle.digest('SHA-256', data).then(function(buf) {
      return Array.prototype.map.call(new Uint8Array(buf), function(b) {
        return ('0' + b.toString(16)).slice(-2);
      }).join('');
    });
  }

  /**
   * Open the lock modal. series = the locked series the shopper clicked, so the
   * code tab checks that series' code. A series without a code can only be
   * unlocked by email, so the modal opens straight on the email tab.
   */
  function openModal(series) {
    var modal = document.getElementById('lock-modal');
    if (!modal) return;
    currentSeries = series || null;
    var hasCode = currentSeries ? !!seriesCodes[currentSeries] : Object.keys(seriesCodes).length > 0;
    var codeTab = modal.querySelector('.js-lock-tab[data-tab="code"]');
    if (codeTab) codeTab.style.display = hasCode ? '' : 'none';
    switchTab(hasCode ? 'code' : 'email');

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the lock modal
   */
  function closeModal() {
    var modal = document.getElementById('lock-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';

      // Reset form states
      var codeError = modal.querySelector('.js-lock-code-error');
      var emailError = modal.querySelector('.js-lock-email-error');
      var emailSuccess = modal.querySelector('.js-lock-email-success');
      var codeInput = modal.querySelector('.js-lock-code-input');
      var emailInput = modal.querySelector('.js-lock-email-input');

      if (codeError) codeError.style.display = 'none';
      if (emailError) emailError.style.display = 'none';
      if (emailSuccess) emailSuccess.style.display = 'none';
      if (codeInput) codeInput.value = '';
      if (emailInput) emailInput.value = '';
    }
    currentSeries = null;
  }

  /**
   * Handle code form submission. The code unlocks whichever series it belongs
   * to; the modal closes once the series the shopper clicked is open.
   */
  function handleCodeSubmit(e) {
    e.preventDefault();

    var input = document.querySelector('.js-lock-code-input');
    var errorEl = document.querySelector('.js-lock-code-error');
    if (!input) return;

    function fail() {
      if (errorEl) errorEl.style.display = 'block';
      input.focus();
      input.select();
    }

    if (!input.value.trim()) { fail(); return; }

    fingerprint(input.value).then(function(hash) {
      var matched = Object.keys(seriesCodes).filter(function(series) {
        return seriesCodes[series] === hash;
      });
      if (!matched.length) { fail(); return; }
      matched.forEach(unlockSeries);
      if (!currentSeries || matched.indexOf(currentSeries) !== -1) {
        closeModal();
        executePendingAction();
      } else {
        fail(); // a real code, but for a different series than the one clicked
      }
    }).catch(fail);
  }

  /**
   * Handle email form submission via Klaviyo API. Unlocks everything.
   */
  function handleEmailSubmit(e) {
    e.preventDefault();

    var form = e.target;
    var emailInput = form.querySelector('.js-lock-email-input');
    var submitButton = form.querySelector('.lock-modal__button');
    var successEl = form.querySelector('.js-lock-email-success');
    var errorEl = form.querySelector('.js-lock-email-error');
    var originalButtonText = submitButton ? submitButton.textContent : '';

    if (!emailInput || !emailInput.value) return;

    if (!config.klaviyoListId) {
      console.warn('Klaviyo List ID not configured');
      unlockAll();
      closeModal();
      executePendingAction();
      return;
    }

    // Hide any previous messages
    if (errorEl) errorEl.style.display = 'none';
    if (successEl) successEl.style.display = 'none';

    // Disable button during submission
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    // Subscribe via Klaviyo AJAX endpoint
    fetch('https://a.klaviyo.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        g: config.klaviyoListId,
        email: emailInput.value,
        $source: 'Locked Products Unlock'
      }).toString()
    })
    .then(function(response) {
      if (response.ok) {
        unlockAll();
        if (successEl) {
          successEl.style.display = 'block';
        }
        // Close modal after short delay
        setTimeout(function() {
          closeModal();
          executePendingAction();
        }, 1500);
      } else {
        throw new Error('Subscription failed');
      }
    })
    .catch(function(error) {
      console.warn('Klaviyo subscription error:', error);
      if (errorEl) {
        errorEl.style.display = 'block';
      }
      // Re-enable button on error
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    });
  }

  /**
   * Execute pending action after unlock
   */
  function executePendingAction() {
    if (pendingAction) {
      if (pendingAction.type === 'navigate') {
        window.location.href = pendingAction.url;
      } else if (pendingAction.type === 'filter' && pendingAction.element) {
        // Re-trigger the filter click
        pendingAction.element.click();
      }
      pendingAction = null;
    }
  }

  /**
   * Switch between tabs
   */
  function switchTab(tabName) {
    var tabs = document.querySelectorAll('.js-lock-tab');
    var panels = document.querySelectorAll('.lock-modal__panel');

    tabs.forEach(function(tab) {
      if (tab.getAttribute('data-tab') === tabName) {
        tab.classList.add('lock-modal__tab--active');
      } else {
        tab.classList.remove('lock-modal__tab--active');
      }
    });

    panels.forEach(function(panel) {
      if (panel.id === 'lock-panel-' + tabName) {
        panel.style.display = 'block';
        panel.classList.add('lock-modal__panel--active');

        // Focus input
        var input = panel.querySelector('.lock-modal__input');
        if (input) {
          setTimeout(function() {
            input.focus();
          }, 100);
        }
      } else {
        panel.style.display = 'none';
        panel.classList.remove('lock-modal__panel--active');
      }
    });
  }

  /**
   * Intercept clicks on locked product cards
   */
  function interceptProductCardClicks() {
    document.addEventListener('click', function(e) {
      // Already unlocked
      if (isAllUnlocked()) return;

      // Check if click is on a locked product link or overlay
      var lockedOverlay = e.target.closest('.product-block__lock-overlay');
      var lockedProduct = e.target.closest('.product-block--locked');
      if (!lockedOverlay && !lockedProduct) return;

      var card = lockedProduct || lockedOverlay.closest('[data-lock-series]');
      var series = card ? card.getAttribute('data-lock-series') : null;

      // Its time passed while the page sat open, or it was unlocked in
      // another tab: open it up instead of asking.
      if (series && isUnlocked(series)) {
        applyUnlock(series);
        return;
      }

      var productLink = lockedProduct ? lockedProduct.querySelector('a[href]') : null;

      // Check if the actual clicked element is a link within the locked product
      var clickedLink = e.target.closest('a[href]');
      if (clickedLink && lockedProduct && lockedProduct.contains(clickedLink)) {
        e.preventDefault();
        e.stopPropagation();

        pendingAction = {
          type: 'navigate',
          url: clickedLink.href
        };

        openModal(series);
      } else if (lockedOverlay) {
        e.preventDefault();
        e.stopPropagation();

        if (productLink) {
          pendingAction = {
            type: 'navigate',
            url: productLink.href
          };
        }

        openModal(series);
      }
    }, true);
  }

  /**
   * Initialize modal event listeners
   */
  function initModalEvents() {
    // Close button clicks
    document.querySelectorAll('.js-lock-modal-close').forEach(function(el) {
      el.addEventListener('click', closeModal);
    });

    // Tab switching
    document.querySelectorAll('.js-lock-tab').forEach(function(tab) {
      tab.addEventListener('click', function() {
        switchTab(this.getAttribute('data-tab'));
      });
    });

    // Code form submission
    var codeForm = document.querySelector('.js-lock-code-form');
    if (codeForm) {
      codeForm.addEventListener('submit', handleCodeSubmit);
    }

    // Email form submission
    var emailForm = document.querySelector('.js-lock-email-form');
    if (emailForm) {
      emailForm.addEventListener('submit', handleEmailSubmit);
    }

    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        var modal = document.getElementById('lock-modal');
        if (modal && modal.style.display === 'flex') {
          closeModal();
        }
      }
    });

    // Clear error on input
    var codeInput = document.querySelector('.js-lock-code-input');
    if (codeInput) {
      codeInput.addEventListener('input', function() {
        var errorEl = document.querySelector('.js-lock-code-error');
        if (errorEl) {
          errorEl.style.display = 'none';
        }
      });
    }
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    refresh();
    initModalEvents();
    interceptProductCardClicks();
  }

  // Expose functions globally for use in custom.js
  window.wigglitzLockIsUnlocked = isUnlocked;       // (series?) -> boolean
  window.wigglitzLockOpenModal = openModal;         // (series?)
  window.wigglitzLockRefresh = refresh;             // (root?) re-apply unlocks
  window.wigglitzLockSetPendingAction = function(action) {
    pendingAction = action;
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
